import { useAtom } from 'jotai'
import { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { videoElementAtom } from '../atoms'
import { Speaker2 } from '@styled-icons/fluentui-system-filled/Speaker2'
import { SpeakerMute } from '@styled-icons/fluentui-system-filled/SpeakerMute'
import { useSpring } from '@react-spring/web'
import { animated } from 'react-spring'

/*======================== React component ===================================*/

const VolumeSlider = (): JSX.Element => {
  const [volume, setVolume] = useState(1)
  const volumeBackup = useRef(1)
  const [muted, setMuted] = useState(false)
  const [videoElement] = useAtom(videoElementAtom)

  const zeroByDrag = () => {
    setVolume(0)
    setMuted(true)
    volumeBackup.current = 1
  }

  const zeroByClickOnIcon = () => {
    volumeBackup.current = volume
    setVolume(0)
    setMuted(true)
  }

  const restoreVolume = () => {
    setVolume(volumeBackup.current)
    setMuted(false)
  }

  useEffect(() => {
    if (!videoElement) {
      return
    }

    videoElement.volume = volume
  }, [videoElement, volume])

  const wrapperRef = useRef<HTMLDivElement>(null)
  const [showSlider, setShowSlider] = useState(false)

  useEffect(() => {
    if (wrapperRef.current) {
      wrapperRef.current.addEventListener('mouseover', () => {
        setShowSlider(true)
      })

      wrapperRef.current.addEventListener('mouseout', () => {
        setShowSlider(false)
      })
    }
  }, [wrapperRef.current])

  const sliderStyle = useSpring({
    width: showSlider ? '100px' : '0px',
    opacity: showSlider ? 1 : 0,
    config: {
      duration: 80,
    },
  })

  return (
    <Wrapper ref={wrapperRef}>
      {muted ? (
        <MutedButton
          onClick={() => {
            if (muted) {
              restoreVolume()
            } else {
              zeroByClickOnIcon()
            }
          }}
        />
      ) : (
        <SpeakerButton
          onClick={() => {
            if (muted) {
              restoreVolume()
            } else {
              zeroByClickOnIcon()
            }
          }}
        />
      )}
      <AnimatedSlider
        style={sliderStyle}
        type="range"
        min={0}
        max={1}
        step={0.01}
        value={volume}
        val={volume}
        onChange={(e: { target: { value: any } }) => {
          const vol = Number(e.target.value)
          console.log(vol)
          if (vol == 0) {
            zeroByDrag()
          } else {
            setVolume(vol)
            setMuted(false)
          }
        }}
      />
    </Wrapper>
  )
}

export default VolumeSlider

/*======================== Styled components =================================*/

const Wrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`

const SpeakerButton = styled(Speaker2)`
  border: none;
  cursor: pointer;
  color: white;
  margin-right: 0.5rem;
  height: 25px;
`

const MutedButton = styled(SpeakerMute)`
  border: none;
  cursor: pointer;
  color: white;
  margin-right: 0.5rem;
  height: 25px;
`

const Slider = styled.input<{ val: number }>`
  -webkit-appearance: none;
  height: 5px;
  border-radius: 5px;
  outline: none;
  -webkit-transition: 0.2s;
  transition: opacity 0.2s;
  background: linear-gradient(
    to right,
    white 0%,
    white ${({ val }) => `${val * 100}%`},
    grey ${({ val }) => `${val * 100}%`},
    grey 100%
  );

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 15px;
    height: 15px;
    border-radius: 50%;
    background: white;
    cursor: pointer;
  }

  &::-moz-range-thumb {
    width: 15px;
    height: 15px;
    border-radius: 50%;
    background: white;
    cursor: pointer;
  }
`

const AnimatedSlider = animated(Slider)
